import { useEffect, useRef, useState } from 'react';
import type { ImageLayer } from '../core/model';
import type { DocumentSession } from '../core/session';
import { UpdateImageFrameCommand } from '../core/image-commands';
import { nativeDocumentImage } from '../public-format/native-images';

export function ImageProperties({ layer, session, onEditPixels }: { layer: ImageLayer; session: DocumentSession; onEditPixels: () => void }) {
  const { width, height, fit } = layer.image.frame;
  const [frameWidth, setWidth] = useState(String(width));
  const [frameHeight, setHeight] = useState(String(height));
  const [frameFit, setFit] = useState(fit);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const pending = useRef<AbortController>();
  useEffect(() => { setWidth(String(width)); setHeight(String(height)); setFit(fit); }, [width, height, fit]);
  useEffect(() => () => { pending.current?.abort(); pending.current = undefined; }, []);
  const source = nativeDocumentImage(session.document, layer);
  const placementLocked = layer.locked || layer.positionLocked;
  const pixelsLocked = layer.locked || layer.pixelsLocked;
  const replace = async (file: File) => {
    const controller = new AbortController(); pending.current = controller;
    setBusy(true); setError(''); setMessage('Loading image…');
    try {
      const binding = session.captureRevision();
      const { replaceImageSource } = await import('../public-format/replace-image-source');
      if (controller.signal.aborted) throw new DOMException('Cancelled.', 'AbortError');
      if (!session.isCurrent(binding)) throw new Error('The document changed. Select the replacement again.');
      const result = await replaceImageSource(session, layer.id, file, controller.signal);
      if (pending.current !== controller) return;
      if (result.outcome === 'rejected') throw new Error(result.reason);
      setMessage(result.outcome === 'changed' ? 'Image replaced. Frame preserved.' : 'Source unchanged.');
    } catch (reason) {
      if (pending.current !== controller) return;
      setMessage(controller.signal.aborted ? 'Replacement cancelled.' : '');
      if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : 'Could not replace this image.');
    } finally {
      if (pending.current === controller) { pending.current = undefined; setBusy(false); }
    }
  };
  return <section className="image-properties" aria-label="Image properties">
    <div className="image-properties-heading"><strong>Image</strong><span>{source.width} × {source.height} px source</span></div>
    <form onSubmit={event => {
      event.preventDefault(); setError(''); setMessage('');
      try {
        const result = session.history.execute(session.document, new UpdateImageFrameCommand(layer.id, { width: Number(frameWidth), height: Number(frameHeight), fit: frameFit }));
        if (result.outcome === 'rejected') throw new Error(result.reason);
        setMessage(result.outcome === 'changed' ? 'Frame updated.' : 'Frame unchanged.');
      } catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not update the frame.'); }
    }}>
      <fieldset disabled={busy || placementLocked}>
        <legend>Frame</legend>
        <div className="image-frame-size">
          <label>Width<input aria-label="Image frame width" type="number" min="1" max="32768" step="1" required value={frameWidth} onChange={event => setWidth(event.target.value)} /></label>
          <label>Height<input aria-label="Image frame height" type="number" min="1" max="32768" step="1" required value={frameHeight} onChange={event => setHeight(event.target.value)} /></label>
        </div>
        <label>Fit<select aria-label="Image fit" value={frameFit} onChange={event => setFit(event.target.value as typeof fit)}>
          <option value="contain">Contain — show all</option><option value="cover">Cover — crop to frame</option><option value="fill">Stretch — fill frame</option>
        </select></label>
        <button type="submit">Apply frame</button>
      </fieldset>
    </form>
    {placementLocked ? <p>Image placement is locked.</p> : null}
    <div className="image-source-actions">
      <button type="button" disabled={busy || pixelsLocked} onClick={onEditPixels}>Edit source pixels…</button>
      <input ref={input} type="file" hidden aria-label="Replace image source file" accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp" onChange={event => {
        const file = event.target.files?.[0]; event.target.value = '';
        if (file && !busy && !pixelsLocked) void replace(file);
      }} />
      <button type="button" disabled={busy || pixelsLocked} onClick={() => input.current?.click()}>Replace image…</button>
      {busy ? <button type="button" onClick={() => pending.current?.abort()}>Cancel</button> : null}
    </div>
    {pixelsLocked ? <p>Image pixels are locked.</p> : null}
    <p>PNG, JPEG or WebP. Replacement keeps this frame and can be undone.</p>
    {message ? <p role="status">{message}</p> : null}
    {error ? <p role="alert" className="image-properties-error">{error}</p> : null}
  </section>;
}
